import {
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";

interface GlasgowFormData {
  GS_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  SEE: string;
  SEE_SCORE: string;
  VERBAL: string;
  VERBAL_SCORE: string;
  MOTOR: string;
  MOTOR_SCORE: string;
  NURSE_NOTE: string;
  GLASGOW_SCORE: number;
}

interface GlassGowScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof GlasgowFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function GlasgowScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: GlassGowScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  // ... (existing code) logic was deleted. Restoring:
  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "GLASGOW_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof GlasgowFormData, value);
      });
    },
  });

  const typedFormData = formData as GlasgowFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof GlasgowFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = useCallback(() => {
    let score =
      Number(typedFormData.SEE_SCORE) +
      Number(typedFormData.VERBAL_SCORE) +
      Number(typedFormData.MOTOR_SCORE);
    setScore(score);
    handleChange("GLASGOW_SCORE", score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData.SEE_SCORE,
    typedFormData.VERBAL_SCORE,
    typedFormData.MOTOR_SCORE,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [
    typedFormData.SEE_SCORE,
    typedFormData.VERBAL_SCORE,
    typedFormData.MOTOR_SCORE,
    handleScoreCalculate,
  ]);

  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.GS_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("glasgow.glasgow-score")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("glasgow.patient-related-factors")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("glasgow.eye-open")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("glasgow.eye-open")}
                  mcpId={cardData.mcpConfig.SEE.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("SEE", val);
                    }
                  }}
                  value={selectedMCPs.SEE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.SEE.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("glasgow.verbal-response")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <Grid xs={12} sm={6} md={6}>
                  <ByzMcParameterDetailSelect
                    title={t("glasgow.verbal-response")}
                    mcpId={cardData.mcpConfig?.VERBAL?.mcpId || 0}
                    multiple={false}
                    handleChange={(val) => {
                      if (!Array.isArray(val)) {
                        handleMCPChange("VERBAL", val);
                      }
                    }}
                    value={selectedMCPs.VERBAL}
                    sx={{ width: "100%" }}
                    cachedData={getMCPParameterDetails(
                      cardData.mcpConfig?.VERBAL?.mcpId || 0
                    )}
                    cacheLoaded={mcpCacheLoaded}
                    readOnly={!extraAccess}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("glasgow.motor-response")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("glasgow.motor-response")}
                  mcpId={cardData.mcpConfig?.MOTOR?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (!Array.isArray(val)) {
                      handleMCPChange("MOTOR", val);
                    }
                  }}
                  value={selectedMCPs.MOTOR}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.MOTOR?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  label={t("score")}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  label={t("note")}
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default GlasgowScoreCreate;
